import React from "react";
import { useNavigate } from "react-router-dom";
import { settingsUrl } from "@presentation/common/settingsRoutes";
import { Card, CardBody } from "@progress/kendo-react-layout";
import { Button } from "@progress/kendo-react-buttons";
import { Grid, GridColumn, GridCustomCellProps } from "@progress/kendo-react-grid";
import { gearIcon, gridIcon, plusOutlineIcon } from "@progress/kendo-svg-icons";
import { useRecentlyVisitedStore } from "@presentation/stores/RecentlyVisitedStore";
import { useWorkspaceContextStore } from "@presentation/stores/WorkspaceContextStore";
import { useWorkspaceFilters } from "@presentation/hooks/useWorkspaceFilters";
import { formatUserDisplayName, useUserDisplayNames } from "@presentation/hooks/useUserDisplayNames";
import { fillCardStyle, pageFillContentStyle, pageHeaderRegionStyle, pageHeaderStyle, pageShellStyle, pageTitleStyle, sectionTitleStyle } from "@presentation/common/pageLayout";

/**
Landing page - the recently-visited projects table (localStorage
only, per-browser) for quick navigation back to a project's jobs or configuration.
*/
const HomePage: React.FC = () => {
  const navigate = useNavigate();
  const filters = useWorkspaceFilters();
  const recentProjects = useRecentlyVisitedStore((state) => state.recentProjects);
  const projects = useWorkspaceContextStore((state) => state.projects);
  const setActiveTeamContext = useWorkspaceContextStore((state) => state.setActiveTeamContext);
  const recentProjectIds = React.useMemo(() => new Set(recentProjects.map((project) => project.projectId)), [recentProjects]);
  const managerUserNames = useUserDisplayNames(
    React.useMemo(() => projects.filter((project) => recentProjectIds.has(project.id)).flatMap((project) => project.managerUserIds ?? []), [projects, recentProjectIds]),
  );
  const rows = React.useMemo(
    () =>
      recentProjects
        .filter((recent) => (!filters.teamId || recent.teamId === filters.teamId) && (filters.scope !== "project" || !filters.projectId || recent.projectId === filters.projectId))
        .map((recent) => {
          const managerUserIds = projects.find((project) => project.id === recent.projectId)?.managerUserIds;
          return {
            ...recent,
            manager: managerUserIds ? managerUserIds.map((userId) => formatUserDisplayName(managerUserNames[userId], userId)).join(", ") || "No manager assigned" : "-",
          };
        }),
    [recentProjects, projects, managerUserNames, filters.scope, filters.teamId, filters.projectId],
  );

  return (
    <div style={pageShellStyle}>
      <div style={pageHeaderRegionStyle}>
        <div style={pageHeaderStyle}>
          <h1 style={pageTitleStyle}>Home</h1>
        </div>
      </div>

      <div style={pageFillContentStyle}>
        {recentProjects.length > 0 && (
          <Card style={fillCardStyle}>
            <CardBody style={{ flex: "1 1 auto", minHeight: 0, display: "flex", flexDirection: "column", padding: 0 }}>
              <h2 style={{ ...sectionTitleStyle, margin: "16px 16px 12px" }}>Recently visited</h2>
              <Grid style={{ flex: "1 1 auto", minHeight: 0 }} data={rows} dataItemKey="projectId">
                <GridColumn field="projectName" title="Project" />
                <GridColumn field="teamName" title="Team" width="220px" />
                <GridColumn field="manager" title="Manager" />
                <GridColumn
                  title=""
                  width="60px"
                  cell={(properties: GridCustomCellProps) => (
                    <td>
                      <Button
                        type="button"
                        fillMode="flat"
                        svgIcon={plusOutlineIcon}
                        title="Set up a job"
                        aria-label="Set up a job"
                        onClick={() => {
                          setActiveTeamContext(properties.dataItem.teamId, properties.dataItem.projectId);
                          navigate("/job-setup");
                        }}
                      />
                    </td>
                  )}
                />
                <GridColumn
                  title=""
                  width="60px"
                  cell={(properties: GridCustomCellProps) => (
                    <td>
                      <Button
                        type="button"
                        fillMode="flat"
                        svgIcon={gridIcon}
                        title="View jobs"
                        aria-label="View jobs"
                        onClick={() => navigate(`/jobs?teamId=${properties.dataItem.teamId}&projectId=${properties.dataItem.projectId}`)}
                      />
                    </td>
                  )}
                />
                <GridColumn
                  title=""
                  width="60px"
                  cell={(properties: GridCustomCellProps) => (
                    <td>
                      <Button
                        type="button"
                        fillMode="flat"
                        svgIcon={gearIcon}
                        title="Project settings"
                        aria-label="Project settings"
                        onClick={() => navigate(settingsUrl({ level: "project", projectId: properties.dataItem.projectId }))}
                      />
                    </td>
                  )}
                />
              </Grid>
            </CardBody>
          </Card>
        )}
      </div>
    </div>
  );
};

export default HomePage;
